import 'url-polyfill/url-polyfill.min'
import 'intersection-observer'
import '@manychat/manyui/src/assets/css/index.css'

import React from 'react'
import * as Sentry from '@sentry/react'
import { Primitive } from '@sentry/types'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import { api } from 'app/initMCApi'
import { store } from 'app/initStore'
import { BillingVersion } from 'common/billing/constants/AccountBillingInfo'
import {
  getAllowedLanguages,
  getCsrfToken,
  getCurrentAccountID,
  getLoggedIn,
  getUserLanguage,
} from 'common/core/selectors/appSelectors'
import { ACCOUNT_ID_REGEX, ID_REGEX } from 'constants/RegExs'
import { initApi } from 'shared/api/initApi'
import { isCustomError } from 'shared/errors/helpers/guards'
import { UsercentricsError } from 'shared/errors/serviceLayerError'
import { isChromium } from 'utils'
import { setAccountIDGetter } from 'utils/accountId'
import { setURLFormatter } from 'utils/api/mcApi/mcApi'
import { RequestError } from 'utils/api/mcApi/RequestError'
import { CustomUserCentricsEvent } from 'utils/getInfoAboutService'
import { setLoggingTasks } from 'utils/longTaskTracker/LongTaskTracker'
import { addSystemParams } from 'utils/middleware/urlUtils'
import { PAGE_SESSION_ID } from 'utils/pageSessionId'
import { replaceIds } from 'utils/replaceIds'

import ReactDOM from 'react-dom/client'
import 'utils/router/typedRouting.init'

import { router } from 'utils/router/router'
import { getComponentByRoute } from 'utils/router/tools'
import {
  hasEventWithUrlWithoutCheckTokenOnBackend,
  hasHotjarFrame,
} from 'utils/services/errorTrackingService/helpers'
import { isSettledForbiddenReport } from 'utils/services/errorTrackingService/settledForbiddenReport'
import { initializeFeatureFlagsServices } from 'utils/services/featureFlagsService'
import { initPostHog } from 'utils/services/posthogService'
import { linkDomain } from 'utils/url'

import { initI18n } from '../i18n/config'

import actions from './actions'
import { initializePostRenderingLogic } from './initApp'
import { initIsMobile } from './initIsMobile'
import Root from './Root'

import 'utils/visualCoverage'

const User = window.__INIT__?.['app.user']
const CurrentAccount = window.__INIT__?.['app.currentAccount']

const IdFromInit = User?.user_id
const PageIdInit = CurrentAccount?.page_id
const isBillingV3 = CurrentAccount?.billing_info?.billing_version === BillingVersion.V3

const USER_ID = IdFromInit ? String(IdFromInit) : undefined
const PAGE_ID = PageIdInit ? String(PageIdInit) : undefined

const IS_LOCAL = process.env.NODE_ENV === 'development'

export const replaceIdsInTransaction = (transaction: string) => transaction.replace(ID_REGEX, 'id')

export const initSentry = (event: CustomUserCentricsEvent | Event) => {
  const hasLoadError = 'detail' in event && event?.detail
  const initialScope = hasLoadError
    ? undefined
    : {
        tags: { pageId: PAGE_ID } as Record<string, Primitive>,
        user: {
          id: USER_ID,
          name: User?.name,
        },
      }
  // @TODO: only used for release and should be removed
  // https://youtrack.manychat.io/issue/BB-2517/Remove-billing-v3-sentry-tag
  if (initialScope && isBillingV3) {
    initialScope.tags.billing_v3 = true
  }

  Sentry.init({
    dsn: `https://2e97b4daccb443d4aa97ccedea318be3@o1241926.ingest.sentry.io/6396134`,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['manychat-app'],
        behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
      }),
      Sentry.extraErrorDataIntegration(),
    ],
    ignoreErrors: [
      // These errors don't strongly affect users and occur in dependencies code
      // https://youtrack.manychat.io/issue/PLATFORM-719
      'ResizeObserver loop completed with undelivered notifications',
      // https://youtrack.manychat.io/issue/TC-1136
      'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    ],
    tracesSampleRate: IS_LOCAL ? 1 : 0.01,
    environment: window.__SENTRY_ENV__?.environment,
    release: window.__SENTRY_ENV__?.release,
    initialScope,
    normalizeDepth: 4,
    beforeSend: (event, hint) => {
      const { originalException: exception, captureContext } = hint
      // Read before the RequestError fallback below, so only the capture call's own fingerprint counts
      const callsiteFingerprint = event.fingerprint

      if (hasHotjarFrame(event)) {
        return null
      }

      const isEventWithUrlWithoutCheckTokenOnBackend = hasEventWithUrlWithoutCheckTokenOnBackend({
        exception,
        captureContext,
      })
      if (isEventWithUrlWithoutCheckTokenOnBackend) {
        return null
      }

      const isRequestError = exception instanceof RequestError
      if (isRequestError && !exception.online) {
        return null
      }

      if (isCustomError(exception) && exception.isReported) {
        return null
      }

      if (isSettledForbiddenReport(exception)) {
        return null
      }

      event.tags = {
        ...event.tags,
        is_custom_error: isCustomError(exception),
        pageSessionId: PAGE_SESSION_ID,
      }

      if (isRequestError) {
        event.tags = {
          ...event.tags,
          request_error: true,
          error_reason: exception.reason,
        }

        if (exception.endpoint) {
          event.transaction = exception.endpoint
        }

        if (!event.fingerprint && exception.endpoint) {
          event.fingerprint = ['{{ default }}', exception.endpoint]
        }
      }

      if (event.request && event.request.url) {
        event.request.url = event.request.url.replace(ACCOUNT_ID_REGEX, '/accountId/')
      }

      if (event.transaction) {
        event.transaction = replaceIds(event.transaction)
      }

      if (isCustomError(exception)) {
        const logOptions = exception.getLogOptions()
        const { fingerprint, scope, section } = logOptions

        event.fingerprint = [...new Set([...fingerprint, ...(callsiteFingerprint ?? [])])]

        event.tags = event.tags ?? {}
        if (scope) {
          event.tags.scope = scope
        }

        if (section) {
          event.tags.section = section
        }

        if (scope && section) {
          event.tags.scopeWithSection = `${scope}/${section}`
        }

        if ('tags' in logOptions && logOptions.tags) {
          Object.assign(event.tags, logOptions.tags)
        }

        if ('extras' in logOptions && logOptions.extras) {
          event.extra = { ...event.extra, ...logOptions.extras }
        }
      }

      // temporary tag to make sure that "Maximum call stack size exceeded" errors are related to translated pages in Chrome
      // https://youtrack.manychat.io/issue/SNF-2084/Investigate-the-recursive-rendering-function-call-issue#focus=Comments-4-113115.0-0
      if (isChromium && document.documentElement.className.includes('translated')) {
        event.tags.googleChromeTranslated = true
      }

      if (isCustomError(exception)) {
        exception.onReported()
      }

      return event
    },
    beforeSendTransaction(event) {
      if (event.transaction) {
        event.transaction = replaceIds(event.transaction)
      }

      return event
    },
  })

  if (window.STATIC_VERSION !== undefined) {
    Sentry.setTag('app_version', window.STATIC_VERSION)
  }

  if (hasLoadError) {
    const exception = new UsercentricsError('Usercentrics load error')
    const { fingerprint } = exception.getLogOptions()

    Sentry.captureException(exception, {
      extra: event.detail as unknown as SafeUnknownObject,
      fingerprint,
      level: 'warning',
    })
  }
}
window.addEventListener('initializedSentry', initSentry)

// API
setURLFormatter((url) => addSystemParams(linkDomain(url), store.getState))
setAccountIDGetter(() => getCurrentAccountID(store.getState()))

initApi({
  getCurrentAccountId: () => getCurrentAccountID(store.getState()),
  getCsrfToken: () => getCsrfToken(store.getState()),
})

// global ref
window.app = {
  store,
  actions,
  // Global ref to the router instance for avoiding cycle deps issues with using router/tools
  router,
}

Sentry.setTag('component', getComponentByRoute())
router.subscribe(() => Sentry.setTag('component', getComponentByRoute()))

initializeFeatureFlagsServices(store.getState())
initPostHog()
initIsMobile()
initI18n(getAllowedLanguages(store.getState()), getUserLanguage(store.getState())).then(() => {
  const $div = document.createElement('div')
  $div.className = 'app'
  const rootEl = document.body.appendChild($div)
  ReactDOM.createRoot(rootEl).render(<Root store={store} api={api} />)

  store.dispatch(initializePostRenderingLogic())
  const isLoggedIn = getLoggedIn(store.getState())
  if (isLoggedIn) {
    setLoggingTasks(store.getState())
  }
})
